import { z } from "zod";

export const ModelTypeSchema = z.enum([
  "text-to-speech",
  "speech-to-text",
  "text-generation",
  "embedding",
  "anonymization",
  "text-classification",
  "audio-enhancement",
  "agent",
  "decision",
]);

export const ModelUnitSchema = z.enum([
  "second",
  "minute",
  "char",
  "token",
  "1ktokens",
  "1kchars",
  "1mtokens",
  "1mchars",
  "1mbytes",
]);

export const ValueCostModelSchema = z.object({
  value: z.number(),
});

export const IOCostModelSchema = z.object({
  input: z.number(),
  output: z.number(),
});

export const CostModelSchema = z.object({
  input: z.number().optional(),
  output: z.number().optional(),
  value: z.number().optional(),
  insights: z.number().optional(),
  anonymization: z.number().optional(),
});

const ParameterVisibilitySchema = z.object({
  playground: z.boolean().default(true),
  agent: z.boolean().default(false),
});

const BaseParameterSchema = z.object({
  name: z.string(),
  label: z.string(),
  required: z.boolean().default(false),
  extra_body: z.boolean().default(false),
  visibility: ParameterVisibilitySchema.optional(),
});

export const RangeParameterSchema = z.object({
  type: z.literal("range"),
  min: z.number(),
  max: z.number(),
  default: z.number().optional(),
  // Explicit slider granularity. Without it the renderer derives a step from
  // the span, which yields fractional values for an integer parameter.
  step: z.number().positive().optional(),
});

export const EnumParameterSchema = z.object({
  type: z.literal("enum"),
  options: z.array(z.string()),
  default: z.string().optional(),
});

export const InputParameterSchema = z.object({
  type: z.literal("input"),
  max_length: z.number().optional(),
  min_length: z.number().optional(),
  pattern: z.string().optional(),
  default: z.string().optional(),
});

export const BooleanParameterSchema = z.object({
  type: z.literal("boolean"),
  default: z.boolean().optional(),
});

export const PresetParameterSchema = z.object({
  type: z.literal("preset"),
  name: z.string(),
});

export const ArrayParameterSchema = z.object({
  type: z.literal("array"),
  item_type: z.enum(["string", "number"]).default("string"),
  max_items: z.number().optional(),
  min_items: z.number().optional(),
  default: z.array(z.string()).optional(),
});

export const BuiltInParameterSchema = z.object({
  type: z.literal("built-in"),
  name: z.string(),
  default: z.unknown().optional(),
});

export const ParameterSchema = BaseParameterSchema.and(
  z.discriminatedUnion("type", [
    RangeParameterSchema,
    EnumParameterSchema,
    InputParameterSchema,
    BooleanParameterSchema,
    PresetParameterSchema,
    ArrayParameterSchema,
    BuiltInParameterSchema,
  ])
);

export type Parameter = z.infer<typeof ParameterSchema>;

export const MODALITIES = ["text", "audio", "image", "file", "json", "decisions"] as const;

export const ModalitiesSchema = z.enum(MODALITIES);

export type Modality = z.infer<typeof ModalitiesSchema>;

export const ArchitetureSchema = z.object({
  input_modalities: z.array(ModalitiesSchema),
  output_modalities: z.array(ModalitiesSchema),
  context_window: z.number().optional(),
});

/**
 * TypeSafe decision capabilities. These are per-model limits: the provider
 * documents 64k tokens for state plus every question and 32k for state plus the
 * largest question, which a single `context_window` cannot express. They must
 * round-trip through the client, never be re-derived from the type alone.
 */
export const DecisionCapabilitiesSchema = z.object({
  question_types: z.array(z.enum(["noul", "choice", "score"])),
  max_total_tokens: z.number(),
  max_state_question_tokens: z.number(),
  max_choice_options: z.number(),
  max_score_options: z.number(),
  min_score_options: z.number(),
});

/**
 * `at_cost` means the model is sold at its provider cost (zero margin) and the
 * pricing decks must mirror the cost price. It is an explicit opt-in policy,
 * never implicit for the whole `decision` type.
 */
export const PricingPolicySchema = z.enum(["at_cost"]);

/**
 * Catalog metadata that must survive every parse. Zod strips unknown keys by
 * default, so aliases and the pricing policy are declared explicitly instead of
 * being silently dropped on read, export or import.
 */
export const ModelMetadataSchema = z.object({
  api_aliases: z.array(z.string()).optional(),
  pricing_policy: PricingPolicySchema.optional(),
}).loose();

// AI-XXX: model ratings used to power the picker's speed/intelligence/cost stars
// and Voice/Chat recommendations. Everything here is optional/nullable — a model
// with no ratings data should render gracefully (dashes instead of stars).
export const ModelRatingsOverridesSchema = z.object({
  voice: z.enum(["include", "exclude"]).nullish(),
  chat: z.enum(["include", "exclude"]).nullish(),
});

export const ModelRatingsSchema = z.object({
  // Stable Artificial Analysis model id — the key used to pull real benchmark
  // data (intelligence index) from the AA Data API. Never guessed.
  aa_id: z.string().nullish(),
  intelligence_index: z.number().nullish(),
  ttft_ms: z.number().nullish(),
  output_tps: z.number().nullish(),
  // Fallback blended price (USD/1M) when the server can't derive price_input/output
  // from the rate deck. Prefer the derived per-token prices when present.
  price_blended: z.number().nullish(),
  source: z.string().nullish(),
  updated_at: z.string().nullish(),
  overrides: ModelRatingsOverridesSchema.nullish(),
});

// Deprecation source of truth: presence of this object (non-null) means the
// model is deprecated. The legacy `deprecated` boolean may still be present
// on older records — see `isDeprecated()` in `model-ratings.ts`.
export const ModelDeprecationSchema = z.object({
  date: z.string().nullish(),
  replacement: z.string().nullish(),
  note: z.string().nullish(),
});

/**
 * Who pays for this model, as decided by the SERVER (AI-1067). The client must
 * never re-derive it by cross-referencing `credential_key` with the org's
 * configured credentials - that deduction is what made the picker read as if
 * BYOK applied to a single model instead of the whole provider.
 *
 * - `supported`: the model is tied to a credential provider at all
 * - `required`: it only exists with the org's own key (no rate-deck price)
 * - `configured`: the org has that provider's credential, so this model is
 *   ALREADY billed by the provider and not by the platform
 */
export const ModelByokSchema = z.object({
  supported: z.boolean(),
  required: z.boolean(),
  configured: z.boolean(),
});

// PROTO-1: how the thread/agent can steer a model's reasoning. Computed by the
// server on read (never persisted, never cached) and absent when the model has
// no controllable reasoning. When a model exposes several, the server prefers
// thinking > enable_thinking > reasoning_effort, so `parameter` is the single
// source of truth for which one to read/write.
export const ThinkingValueSchema = z.object({
  type: z.enum(["disabled", "adaptive", "enabled"]),
  budget_tokens: z.number().optional(),
  display: z.literal("summarized").optional(),
});

export const ReasoningLevelSchema = z.object({
  // ReasoningLevelId, or the raw catalog option when it is not a known id.
  id: z.string(),
  // Exact value to write into `parameters[control.parameter]` for this level.
  value: z.union([z.string(), z.boolean(), ThinkingValueSchema]),
  // Raw values that resolve to this level when reading current parameters.
  matches: z.array(z.union([z.string(), z.boolean()])),
  reasons: z.boolean(),
});

export const ReasoningControlSchema = z.object({
  parameter: z.enum(["reasoning_effort", "thinking", "enable_thinking"]),
  // "off" first when present, then ascending effort. Never empty.
  levels: z.array(ReasoningLevelSchema),
  // Level id the catalog default resolves to (agent parameters absent).
  default_level: z.string(),
});

export const ModelSchema = z.object({
  alias: z.string().optional(),
  priority: z.number().optional(),
  description: z.string(),
  name: z.string(),
  provider: z.string(),
  type: ModelTypeSchema,
  unit: ModelUnitSchema,
  execution_type: z.string(),
  status: z.enum(["active", "inactive", "deprecated"]),
  deprecated: z.boolean().optional(),
  experimental: z.boolean().optional(),
  credential_key: z.string().nullish(),
  parameters: z.array(ParameterSchema).optional(),
  architecture: ArchitetureSchema.optional(),
  capabilities: z
    .object({
      tools: z.boolean().default(false),
      reasoning: z.boolean().default(false),
      structured_output: z.boolean().default(false),
      streaming: z.boolean().default(false),
      vision: z.boolean().default(false),
      pdf: z.boolean().default(false),
      audio_input: z.boolean().default(false),
      // AI-1198: WebSocket-only; the REST transcription API refuses it.
      realtime: z.boolean().default(false),
      // How much of the reasoning the provider actually exposes through the
      // gateway. Absent on an older server: treat as unprobed, not as "none".
      reasoning_visibility: z
        .enum(["full", "summary", "tokens_only", "none", "unprobed"])
        .optional(),
      // Decision models only. Absent on every other type.
      decision: DecisionCapabilitiesSchema.optional(),
    })
    .optional(),
  metadata: ModelMetadataSchema.optional(),
  // Editorial uses set by the admin, not measured. Known values: "realtime"
  // (latency-bound, e.g. voice) and "general" (no strict latency budget, e.g.
  // chat). Tolerant on purpose: unknown strings parse and are ignored where read.
  // Optional, not defaulted: models are not parsed on every read path, so the
  // type must admit a missing field. Readers use `?? []`.
  recommended_for: z.array(z.string()).optional(),
  // unix seconds of the model's first activation (same meaning as OpenAI's
  // `created`). Absent on models never activated.
  created: z.number().nullish(),
  ratings: ModelRatingsSchema.nullish(),
  deprecation: ModelDeprecationSchema.nullish(),
  // USD per 1M tokens. May be absent even for active models — degrade gracefully.
  price_input: z.number().nullish(),
  price_output: z.number().nullish(),
  // Nullish on purpose: an older server simply omits it, and everything must
  // degrade to the platform-billed reading.
  byok: ModelByokSchema.nullish(),
  // false ONLY for a model that requires the org's own key and has no
  // credential configured. Anything else (including `null` and an old server
  // that omits the field) is usable. Nullish like `byok`, for the same reason:
  // an absent answer must never read as "unavailable".
  available: z.boolean().nullish(),
  // Absent on an older server, and on models whose reasoning is not steerable.
  reasoning_control: ReasoningControlSchema.optional(),
});

export const ModelSubscriptionSchema = z.object({
  model_name: z.string().max(60),
  subscription_name: z.string().max(40),
  cost_model: CostModelSchema,
  currency: z.string().length(3),
  model: ModelSchema,
});

export type Model = z.infer<typeof ModelSchema>;
export type ModelType = z.infer<typeof ModelTypeSchema>;
export type ModelSubscription = z.infer<typeof ModelSubscriptionSchema>;
export type CostModel = z.infer<typeof CostModelSchema>;
export type ValueCostModel = z.infer<typeof ValueCostModelSchema>;
export type IOCostModel = z.infer<typeof IOCostModelSchema>;
export type ModelUnit = z.infer<typeof ModelUnitSchema>;
export type ModelRatings = z.infer<typeof ModelRatingsSchema>;
export type ModelRatingsOverrides = z.infer<typeof ModelRatingsOverridesSchema>;
export type ModelDeprecation = z.infer<typeof ModelDeprecationSchema>;
export type ModelByok = z.infer<typeof ModelByokSchema>;
export type DecisionCapabilities = z.infer<typeof DecisionCapabilitiesSchema>;
export type PricingPolicy = z.infer<typeof PricingPolicySchema>;
export type ModelMetadata = z.infer<typeof ModelMetadataSchema>;
export type ThinkingValue = z.infer<typeof ThinkingValueSchema>;
export type ReasoningLevel = z.infer<typeof ReasoningLevelSchema>;
export type ReasoningControl = z.infer<typeof ReasoningControlSchema>;

export function getModelName(
  model?: Model,
  options?: {
    preferName?: boolean;
  }
): string {
  if (!model) return "";

  if (options?.preferName) return model.name;

  return model?.alias ?? model.name;
}

/**
 * Providers that are gateways: the catalog alias is the user-facing name and
 * the raw upstream id is an implementation detail. opencode-go behaves like
 * OpenRouter here, so the agent surfaces stay consistent across both.
 */
export function isAliasProvider(provider?: string | null): boolean {
  const key = provider?.trim().toLowerCase();
  return key === "openrouter" || key === "opencode-go";
}

/**
 * Display name for a model in the agent surfaces. Gateway providers show the
 * alias; everyone else shows the model name.
 */
export function getModelDisplayName(model?: Model): string {
  if (!model) return "";
  return isAliasProvider(model.provider) ? getModelName(model) : model.name;
}
